import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import {
  ActivityAction,
  coupleSwitches,
  formatPhone,
  REGIONS,
  type AdminRegionStats,
  type AdminUser,
  type ApproveUserBody,
  type Region,
  type UpdateUserBody,
  type User,
  type UserType,
} from '@photos/shared';
import { useAdminActivity, useAdminRegions, useAdminUserAction, useAdminUsers, eventUsage, useEventAction, useNav, useRemoveEvent } from '../../api/hooks';
import { navTagCounts } from '../../components/SearchBox';
import { ProfileDialog } from '../../components/dialogs/ProfileDialog';
import { ChurchOptions, FellowshipSelect, ServicePicker, useProfileTags } from '../../components/GroupPickers';
import { AreaSwitcher, Brand, SessionUserMenu, useHeaderHeight } from '../../components/Header';
import { Icon, type IconName } from '../../components/Icon';
import { Modal } from '../../components/Modal';
import { TagChip } from '../../components/TagChip';
import { Who } from '../../components/Who';
import { fmtRelative, fmtSize } from '../../lib/format';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toasts';
import { NoAccess } from '../NoAccess';
import { ActivityEntries } from '../studio/ActivityPage';

type Permissions = Pick<User, 'type' | 'canRelease' | 'canSeeRestricted' | 'isAdmin'>;
type SwitchKey = 'canRelease' | 'canSeeRestricted' | 'isAdmin';

/** Switch columns after Type. Can release only applies to media ("—" for users). */
const SWITCHES: { key: SwitchKey; label: string; help: string }[] = [
  { key: 'canRelease', label: 'Can release', help: 'Release and unrelease photos (media only)' },
  { key: 'canSeeRestricted', label: 'Sees restricted photos', help: 'See restricted photos (and mark photos restricted, if media)' },
  { key: 'isAdmin', label: 'Admin', help: 'The Admin area only: it doesn’t include media or any other switch' },
];

const TYPE_HELP = 'Media: upload, tag and organize in every album (Studio). User: Browse only, plus any albums they were given access to.';

/** On/off switch (a checkbox styled as a switch). */
function Switch({ on, label, disabled, title, onChange }: { on: boolean; label: string; disabled?: boolean; title?: string; onChange(on: boolean): void }) {
  return (
    <input
      type="checkbox"
      role="switch"
      className="switch"
      checked={on}
      disabled={disabled}
      aria-label={label}
      title={title}
      onChange={(e) => onChange(e.target.checked)}
    />
  );
}

/** User type picker: "User" or "Media". */
function TypeSelect({ value, label, disabled, onChange }: { value: UserType; label: string; disabled?: boolean; onChange(t: UserType): void }) {
  return (
    <select value={value} aria-label={label} title={TYPE_HELP} disabled={disabled} onChange={(e) => onChange(e.target.value as UserType)}>
      <option value="user">User</option>
      <option value="media">Media</option>
    </select>
  );
}

/** The switch cells of a row: Can release only for media. */
function SwitchCells({ value, name, disabled, adminLocked, onChange }: { value: Permissions; name: string; disabled?: boolean; adminLocked?: boolean; onChange(key: SwitchKey, on: boolean): void }) {
  return (
    <>
      {SWITCHES.map((s) => (
        <td key={s.key}>
          {s.key === 'canRelease' && value.type !== 'media' ? (
            <span style={{ color: 'var(--muted)' }} title="Only media can release">
              —
            </span>
          ) : (
            <Switch
              on={value[s.key]}
              disabled={disabled || (s.key === 'isAdmin' && adminLocked)}
              title={s.key === 'isAdmin' && adminLocked ? 'You can’t remove your own admin access' : s.help}
              label={`${s.label} (${name})`}
              onChange={(on) => onChange(s.key, on)}
            />
          )}
        </td>
      ))}
    </>
  );
}

/* ---------------- access requests ---------------- */

/** What the person filled in on the request form (read-only: they edit it themselves later in My profile). */
function SubmittedProfile({ u }: { u: AdminUser }) {
  const { tagIndex } = useSession();
  const groups = [...(u.fellowship ? [u.fellowship] : []), ...u.services];
  return (
    <>
      <td>
        {u.region ?? '—'}
        <br />
        <small style={{ color: 'var(--muted)' }}>{u.church ? tagIndex.labelOf(u.church) : 'No church'}</small>
      </td>
      <td>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {groups.length ? groups.map((g) => <TagChip key={g} tag={g} />) : <span style={{ color: 'var(--muted)' }}>—</span>}
        </div>
      </td>
    </>
  );
}

/** An access request: the submitted profile (read-only), then permissions only — the type and the switches. */
function RequestRow({ u }: { u: AdminUser }) {
  const [perms, setPerms] = useState<Permissions>({ type: 'user', canRelease: false, canSeeRestricted: false, isAdmin: false });
  const action = useAdminUserAction();
  const toast = useToast();
  const set = (patch: Partial<Permissions>) => setPerms((p) => ({ ...p, ...coupleSwitches(p, patch) }));

  const approve = () => {
    const body: ApproveUserBody = perms;
    action.mutate(
      { id: u.id, action: 'approve', body },
      {
        onSuccess: () => toast({ icon: 'check', title: `${u.name} approved`, sub: 'They can now see released photos.' }),
        onError: (e) => toast({ icon: 'info', title: 'Couldn’t approve', sub: e.message }),
      },
    );
  };
  const deny = () =>
    action.mutate(
      { id: u.id, action: 'deny' },
      {
        onSuccess: () => toast({ icon: 'x', title: `${u.name} denied`, sub: 'Their request was declined.' }),
        onError: (e) => toast({ icon: 'info', title: 'Couldn’t deny', sub: e.message }),
      },
    );

  return (
    <tr>
      <td>
        <Who name={u.name} sub={u.email} />
        {u.phone && <small className="phone-line">{formatPhone(u.phone)}</small>}
      </td>
      <SubmittedProfile u={u} />
      <td style={{ maxWidth: 220 }}>{u.requestReason || '—'}</td>
      <td>
        <TypeSelect value={perms.type} label={`Type for ${u.name}`} onChange={(type) => set({ type })} />
      </td>
      <SwitchCells value={perms} name={u.name} onChange={(key, on) => set({ [key]: on })} />
      <td style={{ whiteSpace: 'nowrap' }}>
        <button type="button" className="btn sm primary" disabled={action.isPending} onClick={approve}>
          Approve
        </button>{' '}
        <button type="button" className="btn sm ghost" disabled={action.isPending} onClick={deny}>
          Deny
        </button>
      </td>
    </tr>
  );
}

function SwitchHeads() {
  return (
    <>
      <th title={TYPE_HELP}>Type</th>
      {SWITCHES.map((s) => (
        <th key={s.key} title={s.help}>
          {s.label}
        </th>
      ))}
    </>
  );
}

function RequestsTab({ users }: { users: AdminUser[] }) {
  const pending = users.filter((u) => u.status === 'pending');
  if (!pending.length)
    return (
      <div className="empty">
        <h3>All caught up</h3>No pending access requests.
      </div>
    );
  return (
    <>
      <div className="tscroll">
        <table className="t">
          <thead>
            <tr>
              <th>Person</th>
              <th>Region · Church</th>
              <th>Groups</th>
              <th>Reason</th>
              <SwitchHeads />
              <th />
            </tr>
          </thead>
          <tbody>
            {pending.map((u) => (
              <RequestRow key={u.id} u={u} />
            ))}
          </tbody>
        </table>
      </div>
      <p className="help" style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 10 }}>
        Anyone can sign in and request access. They see nothing until an admin approves them. Region, church and groups are what they filled in
        (they can change them in My profile; you can edit them under Users after approval). Approved users see released photos. Type Media adds
        Studio with every album (upload, tag, organize); Can release is for media only. Users get Studio only for albums they are given access to
        (an invite link, or Studio → Album access).
      </p>
    </>
  );
}

/* ---------------- users ---------------- */

function UsersTab({ users }: { users: AdminUser[] }) {
  const { user: me, tagIndex } = useSession();
  const action = useAdminUserAction();
  const toast = useToast();
  const members = users.filter((u) => u.status !== 'pending').sort((a, b) => a.name.localeCompare(b.name));
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const update = (u: AdminUser, body: UpdateUserBody, done: string) =>
    action.mutate(
      { id: u.id, action: 'update', body },
      {
        onSuccess: () => toast({ icon: 'check', title: done }),
        onError: (e) => toast({ icon: 'info', title: 'Couldn’t save', sub: e.message }),
      },
    );

  const reactivate = (u: AdminUser) =>
    action.mutate(
      { id: u.id, action: 'reactivate' },
      {
        onSuccess: () => toast({ icon: 'check', title: `${u.name} is active again`, sub: 'Their switches are as they were.' }),
        onError: (e) => toast({ icon: 'info', title: 'Couldn’t reactivate', sub: e.message }),
      },
    );

  const suspend = (u: AdminUser) => {
    if (!window.confirm(`Suspend ${u.name} from Photos? They won’t see any photos until an admin reactivates them. Their JBCH USA account and other apps aren’t affected.`)) return;
    action.mutate(
      { id: u.id, action: 'suspend' },
      {
        onSuccess: () => toast({ icon: 'lock', title: `${u.name} is suspended from Photos` }),
        onError: (e) => toast({ icon: 'info', title: 'Couldn’t suspend', sub: e.message }),
      },
    );
  };

  return (
    <>
      <div className="tscroll">
        <table className="t">
          <thead>
            <tr>
              <th>Name</th>
              <th>Region · Church</th>
              <th>Groups</th>
              <SwitchHeads />
              <th title="Albums a user can upload and tag in (managed in Studio → Album access). Media have every album.">Albums</th>
              <th>Last active</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {members.map((u) => {
              const self = u.id === me.id;
              const suspended = u.status === 'suspended';
              return (
                <tr key={u.id} className={suspended ? 'suspended' : undefined}>
                  <td>
                    <Who name={u.name} sub={u.email} />
                    {u.phone && <small className="phone-line">{formatPhone(u.phone)}</small>}
                    {suspended && (
                      <span className="pill" style={{ color: 'var(--danger)', marginTop: 4 }}>
                        <Icon name="lock" />
                        Suspended
                      </span>
                    )}
                  </td>
                  <td>
                    {u.region ?? '—'}
                    <br />
                    <small style={{ color: 'var(--muted)' }}>{u.church ? tagIndex.labelOf(u.church) : 'No church'}</small>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                      {[...(u.fellowship ? [u.fellowship] : []), ...u.services].map((g) => (
                        <TagChip key={g} tag={g} />
                      ))}
                      {!u.fellowship && !u.services.length && <span style={{ color: 'var(--muted)' }}>—</span>}
                      <button type="button" className="btn sm" aria-label={`Edit profile of ${u.name}`} onClick={() => setEditing(u)}>
                        Edit
                      </button>
                    </div>
                  </td>
                  <td>
                    <TypeSelect
                      value={u.type}
                      label={`Type for ${u.name}`}
                      disabled={action.isPending}
                      onChange={(type) => update(u, { type }, `${u.name}: ${type === 'media' ? 'Media' : 'User'}`)}
                    />
                  </td>
                  <SwitchCells
                    value={u}
                    name={u.name}
                    disabled={action.isPending}
                    adminLocked={self}
                    onChange={(key, on) => update(u, { [key]: on }, `${u.name}: ${SWITCHES.find((s) => s.key === key)!.label} ${on ? 'on' : 'off'}`)}
                  />
                  <td>
                    {u.type === 'media' ? (
                      <span style={{ color: 'var(--muted)' }}>All albums</span>
                    ) : u.albumAccess.length ? (
                      <span title={u.albumAccess.map((a) => a.name).join('\n')} aria-label={`Albums: ${u.albumAccess.map((a) => a.name).join(', ')}`}>
                        {u.albumAccess.length === 1 ? '1 album' : `${u.albumAccess.length} albums`}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--muted)' }}>—</span>
                    )}
                  </td>
                  <td style={{ color: 'var(--muted)' }}>{fmtRelative(u.lastActiveAt)}</td>
                  <td className="row-actions">
                    {suspended ? (
                      <button type="button" className="btn sm" onClick={() => reactivate(u)} disabled={action.isPending}>
                        Reactivate
                      </button>
                    ) : (
                      !self && (
                        <button type="button" className="btn sm ghost" onClick={() => suspend(u)} disabled={action.isPending}>
                          Suspend from Photos
                        </button>
                      )
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editing && <ProfileEditDialog u={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

/** An admin edits someone's profile: region, church, fellowship group and service groups. */
function ProfileEditDialog({ u, onClose }: { u: AdminUser; onClose(): void }) {
  const { churches } = useProfileTags();
  const [region, setRegion] = useState<Region>(u.region ?? 'East');
  const [church, setChurch] = useState(u.church);
  const [fellowship, setFellowship] = useState(u.fellowship);
  const [services, setServices] = useState(u.services);
  const action = useAdminUserAction();
  const toast = useToast();
  const save = () =>
    action.mutate(
      { id: u.id, action: 'update', body: { region, church, fellowship, services } },
      {
        onSuccess: () => {
          onClose();
          toast({ icon: 'check', title: `Updated ${u.name}’s profile` });
        },
        onError: (e) => toast({ icon: 'info', title: 'Couldn’t save', sub: e.message }),
      },
    );
  return (
    <Modal onClose={onClose} labelledBy="profile-edit-title">
      <h3 id="profile-edit-title">Profile of {u.name}</h3>
      <p className="help">One fellowship group at most (e.g. Fathers), and any number of service groups (e.g. Choir, Translation).</p>
      <div className="field">
        <label htmlFor="pe-region">Region</label>
        <select id="pe-region" className="inp" value={region} onChange={(e) => setRegion(e.target.value as Region)}>
          {REGIONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="pe-church">Church</label>
        <select id="pe-church" className="inp" value={church} onChange={(e) => setChurch(e.target.value)}>
          <option value="">No church</option>
          <ChurchOptions churches={churches} />
        </select>
      </div>
      <div className="field">
        <label htmlFor="pe-fellowship">Fellowship group</label>
        <FellowshipSelect id="pe-fellowship" className="inp" value={fellowship} onChange={setFellowship} label="Fellowship group" />
      </div>
      <div className="field">
        <label>Service groups</label>
        <ServicePicker value={services} onChange={setServices} label={`Service groups for ${u.name}`} />
      </div>
      <div className="foot">
        <button type="button" className="btn" data-close onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn primary" disabled={action.isPending} onClick={save}>
          Save
        </button>
      </div>
    </Modal>
  );
}

/* ---------------- regions ---------------- */

const REGION_COLOR = '#1d7488';

function RegionCard({ r }: { r: AdminRegionStats }) {
  const icon: IconName = r.region ? 'church' : 'tag';
  const color = r.region ? REGION_COLOR : '#64748b';
  return (
    <div className="space">
      <div className="hd">
        <span className="ic" style={{ background: `color-mix(in srgb,${color} 14%,transparent)`, color }}>
          <Icon name={icon} />
        </span>
        <h4>{r.region ? `${r.label} region` : 'Untagged'}</h4>
      </div>
      <p>{r.tag ? <>Photos tagged #{r.tag}.</> : 'Photos without a region tag. Add #east, #central or #west to sort them.'}</p>
      <div className="row">
        <span>Photos</span>
        <b>
          {r.photoCount.toLocaleString('en-US')} · {fmtSize(r.bytes)}
        </b>
      </div>
      <div className="row">
        <span>Unreleased</span>
        <b>{r.unreleased.toLocaleString('en-US')}</b>
      </div>
      <div className="row">
        <span>Restricted</span>
        <b>{r.restricted.toLocaleString('en-US')}</b>
      </div>
      <div className="row">
        <span>Albums</span>
        <b>{r.albums.toLocaleString('en-US')}</b>
      </div>
      <div className="row">
        <span>{r.region ? 'Users in region' : 'Users without a region'}</span>
        <b>{r.members}</b>
      </div>
    </div>
  );
}

function RegionsTab() {
  const regions = useAdminRegions();
  if (regions.isError)
    return (
      <div className="empty">
        <h3>Couldn’t load regions</h3>
        {regions.error.message}
      </div>
    );
  if (!regions.data)
    return (
      <div className="sentinel">
        <div className="spinner" />
      </div>
    );
  return (
    <>
      <p style={{ color: 'var(--muted)', margin: '0 0 14px' }}>
        Regions are <b>tags</b>: they help you find photos, they don’t decide who can see them. Visibility (unreleased, released, restricted) decides that.
      </p>
      <div className="spaces">
        {regions.data.regions.map((r) => (
          <RegionCard key={r.tag ?? 'untagged'} r={r} />
        ))}
      </div>
    </>
  );
}

/* ---------------- lists ---------------- */


/** Events: admins add, rename (label only; the #name stays so photos keep working) and delete unused ones. */
function EventsCard() {
  const { tagIndex } = useSession();
  const nav = useNav('all');
  const counts = useMemo(() => navTagCounts(nav.data), [nav.data]);
  const action = useEventAction();
  const toast = useToast();
  const [label, setLabel] = useState('');
  const [editing, setEditing] = useState<{ name: string; label: string } | null>(null);
  const events = [...tagIndex.registry.values()].filter((t) => t.category === 'event').sort((a, b) => a.label.localeCompare(b.label));
  const fail = (title: string) => (e: Error) => toast({ icon: 'info', title, sub: e.message });

  const add = () =>
    action.mutate(
      { action: 'add', label: label.trim() },
      {
        onSuccess: () => {
          toast({ icon: 'check', title: `Added ${label.trim()}` });
          setLabel('');
        },
        onError: fail('Couldn’t add the event'),
      },
    );
  const rename = () =>
    editing &&
    action.mutate(
      { action: 'rename', name: editing.name, label: editing.label.trim() },
      { onSuccess: () => setEditing(null), onError: fail('Couldn’t rename the event') },
    );
  // Remove is always possible: used events become free tags again (photos and albums keep them), unused ones go away.
  const removeEvent = useRemoveEvent();
  const [removing, setRemoving] = useState<{ name: string; label: string; photos: number; albums: number } | null>(null);
  const askRemove = async (name: string, eventLabel: string) => {
    try {
      setRemoving({ name, label: eventLabel, ...(await eventUsage(name)) });
    } catch (e) {
      fail('Couldn’t check the event')(e as Error);
    }
  };
  const confirmRemove = () =>
    removing &&
    removeEvent.mutate(removing.name, {
      onSuccess: (r) => {
        toast({
          icon: 'trash',
          title: `Removed ${removing.label}${r.result === 'demoted' ? ' from events' : ''}`,
          sub: r.result === 'demoted' ? `Photos keep #${removing.name}.` : undefined,
        });
        setRemoving(null);
      },
      onError: fail('Couldn’t remove the event'),
    });

  return (
    <div className="space events-card">
      <div className="hd">
        <h4>Events</h4>
        <span style={{ marginLeft: 'auto', color: 'var(--muted)', fontSize: 12.5 }}>{events.length}</span>
      </div>
      <p>Events appear in Browse automatically once photos are tagged with the event and a year.</p>
      <ul className="event-list">
        {events.map((t) => {
          const used = (counts.get(t.name) ?? 0) > 0;
          return (
            <li key={t.name}>
              {editing?.name === t.name ? (
                <>
                  <input
                    className="inp sm"
                    aria-label={`New label for #${t.name}`}
                    autoFocus
                    maxLength={64}
                    value={editing.label}
                    onChange={(e) => setEditing({ name: t.name, label: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && rename()}
                  />
                  <button type="button" className="btn sm primary" disabled={!editing.label.trim() || action.isPending} onClick={rename}>
                    Save
                  </button>
                  <button type="button" className="btn sm ghost" onClick={() => setEditing(null)}>
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <TagChip tag={t.name} extra={<span className="auto"> {t.label}</span>} />
                  {used && <small style={{ color: 'var(--muted)' }}>{counts.get(t.name)} photos</small>}
                  <span style={{ flex: 1 }} />
                  <button type="button" className="btn sm ghost" aria-label={`Rename ${t.label}`} title="Rename" onClick={() => setEditing({ name: t.name, label: t.label })}>
                    <Icon name="pencil" />
                  </button>
                  <button type="button" className="btn sm ghost" aria-label={`Remove ${t.label}`} title="Remove" onClick={() => void askRemove(t.name, t.label)}>
                    <Icon name="trash" />
                  </button>
                </>
              )}
            </li>
          );
        })}
      </ul>
      <div className="event-add">
        <input
          className="inp sm"
          aria-label="New event"
          placeholder="New event, e.g. Youth Conference"
          maxLength={64}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && label.trim() && add()}
        />
        <button type="button" className="btn sm primary" disabled={!label.trim() || action.isPending} onClick={add}>
          <Icon name="plus" />
          Add event
        </button>
      </div>
      {removing && (
        <Modal onClose={() => setRemoving(null)} labelledBy="remove-event-title">
          <h3 id="remove-event-title">
            {removing.photos || removing.albums ? `Remove “${removing.label}” from events?` : `Remove “${removing.label}”?`}
          </h3>
          <p className="help">
            {removing.photos || removing.albums
              ? `${removing.photos} ${removing.photos === 1 ? 'photo keeps' : 'photos keep'} the #${removing.name} tag${removing.albums ? ` (and ${removing.albums} ${removing.albums === 1 ? 'album keeps it' : 'albums keep it'} in their album tags)` : ''}; it just won’t appear under Events.`
              : 'No photos use it.'}
          </p>
          <div className="foot">
            <button type="button" className="btn" data-close onClick={() => setRemoving(null)}>
              Cancel
            </button>
            <button type="button" className="btn primary danger-fill" disabled={removeEvent.isPending} onClick={confirmRemove}>
              Remove
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const LISTS: { category: 'region' | 'church' | 'fellowship' | 'service'; label: string }[] = [
  { category: 'region', label: 'Regions' },
  { category: 'church', label: 'Churches' },
  { category: 'fellowship', label: 'Fellowship groups' },
  { category: 'service', label: 'Service groups' },
];

function ListsTab() {
  const { tagIndex } = useSession();
  return (
    <>
      <div className="notice">
        <Icon name="info" />
        <span>
          Regions, churches and groups are managed in the database (seeded and maintained by an admin data tool), not from the app. Events are managed
          here by admins.
        </span>
      </div>
      <div className="spaces">
        <EventsCard />
        {LISTS.map((l) => {
          const tags = [...tagIndex.registry.values()].filter((t) => t.category === l.category).sort((a, b) => a.label.localeCompare(b.label));
          return (
            <div className="space" key={l.category}>
              <div className="hd">
                <h4>{l.label}</h4>
                <span style={{ marginLeft: 'auto', color: 'var(--muted)', fontSize: 12.5 }}>{tags.length}</span>
              </div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                {tags.map((t) => (
                  <span key={t.name} title={t.aliases.length ? `Also: ${t.aliases.join(', ')}` : undefined}>
                    <TagChip tag={t.name} extra={<span className="auto"> {t.label}</span>} />
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ---------------- activity ---------------- */

const ACTION_LABEL: Record<ActivityAction, string> = {
  'album.create': 'Album created',
  'album.update': 'Album renamed or edited',
  'album.delete': 'Album deleted',
  'photos.upload': 'Uploaded',
  'photos.release': 'Released',
  'photos.unrelease': 'Unreleased',
  'photos.restrict': 'Marked restricted',
  'photos.unrestrict': 'Unrestricted',
  'photos.move': 'Moved between albums',
  'photos.add': 'Added to album',
  'photos.copy': 'Copied to album',
  'photos.remove': 'Removed from album',
  'photos.tag': 'Tagged',
  'photos.pick': 'Picked',
  'photos.trash': 'Moved to Trash',
  'photos.glean': 'Added from Flock',
  'photos.restore': 'Restored from Trash',
  'photos.delete': 'Deleted permanently',
  'invite.create': 'Invite created',
  'invite.join': 'Joined by invite',
  'invite.revoke': 'Invite revoked',
  'collaborator.add': 'Album access given',
  'collaborator.remove': 'Album access removed',
  undo: 'Undo',
};

function ActivityTab({ users }: { users: AdminUser[] }) {
  const [actorId, setActorId] = useState('');
  const [action, setAction] = useState<ActivityAction | ''>('');
  const [albumId, setAlbumId] = useState('');
  const activity = useAdminActivity({ actorId: actorId || undefined, action: action || undefined, albumId: albumId || undefined });
  const items = activity.data?.pages.flatMap((p) => p.items) ?? [];
  // Albums seen in the log so far (the album filter works for admins without Studio access too).
  const albums = useRef(new Map<string, string>());
  for (const e of items) if (e.albumId && e.albumName) albums.current.set(e.albumId, e.albumName);
  const albumOptions = [...albums.current].sort((a, b) => a[1].localeCompare(b[1]));
  return (
    <>
      <div className="stoolbar">
        <select className="inp sm" aria-label="Person" value={actorId} onChange={(e) => setActorId(e.target.value)}>
          <option value="">Everyone</option>
          {users
            .filter((u) => u.status !== 'pending')
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
        </select>
        <select className="inp sm" aria-label="Action" value={action} onChange={(e) => setAction(e.target.value as ActivityAction | '')}>
          <option value="">All actions</option>
          {ActivityAction.options.map((a) => (
            <option key={a} value={a}>
              {ACTION_LABEL[a]}
            </option>
          ))}
        </select>
        <select className="inp sm" aria-label="Album" value={albumId} onChange={(e) => setAlbumId(e.target.value)}>
          <option value="">All albums</option>
          {albumOptions.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
      </div>
      {activity.isError ? (
        <div className="empty">
          <h3>Couldn’t load activity</h3>
          {activity.error.message}
        </div>
      ) : !activity.data ? (
        <div className="sentinel">
          <div className="spinner" />
        </div>
      ) : (
        <ActivityEntries items={items} />
      )}
      {activity.hasNextPage && (
        <div className="sentinel">
          <button type="button" className="btn sm" onClick={() => void activity.fetchNextPage()} disabled={activity.isFetchingNextPage}>
            Show more
          </button>
        </div>
      )}
    </>
  );
}

/* ---------------- layout ---------------- */

type AdminTab = 'requests' | 'users' | 'regions' | 'lists' | 'activity';

function AdminHeader() {
  const ref = useRef<HTMLElement>(null);
  const [profile, setProfile] = useState(false);
  useHeaderHeight(ref);
  return (
    <header className="top" ref={ref}>
      <div className="top-row studio-row">
        <div className="top-left">
          <Brand to="/admin" label="Admin" />
        </div>
        <div className="actions">
          <AreaSwitcher current="admin" />
          <SessionUserMenu onProfile={() => setProfile(true)} />
        </div>
      </div>
      {profile && <ProfileDialog onClose={() => setProfile(false)} />}
    </header>
  );
}

/** `/admin/*`: its own minimal layout (no sidebar, search or banner) with tabs. Non-admins get a plain notice. */
export function AdminApp() {
  const { me } = useSession();
  const users = useAdminUsers(me.capabilities.admin);
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    document.documentElement.dataset.source = 'glean';
  }, []);
  const list = useMemo(() => users.data ?? [], [users.data]);
  if (!me.capabilities.admin) return <NoAccess area="Admin" />;

  const tab = (location.pathname.split('/')[2] || 'requests') as AdminTab;
  const pending = list.filter((u) => u.status === 'pending').length;
  const TABS: [AdminTab, string, number?][] = [
    ['requests', 'Access requests', pending],
    ['users', 'Users'],
    ['regions', 'Regions'],
    ['lists', 'Lists'],
    ['activity', 'Activity'],
  ];
  const needsUsers = tab === 'requests' || tab === 'users' || tab === 'activity';

  return (
    <>
      <AdminHeader />
      <main className="wrap">
        <div className="toolbar">
          <h1>Admin</h1>
          <span className="meta">Users and permissions, regions, lists and the full activity log</span>
        </div>
        <div className="subtabs" role="tablist">
          {TABS.map(([k, label, n]) => (
            <button type="button" key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => navigate(`/admin/${k}`)}>
              {label}
              {n ? <span className="n">{n}</span> : null}
            </button>
          ))}
        </div>
        {users.isError && needsUsers ? (
          <div className="empty">
            <h3>Couldn’t load users</h3>
            {users.error.message}
          </div>
        ) : !users.data && needsUsers ? (
          <div className="sentinel">
            <div className="spinner" />
          </div>
        ) : (
          <Routes>
            <Route index element={<Navigate to="requests" replace />} />
            <Route path="requests" element={<RequestsTab users={list} />} />
            <Route path="users" element={<UsersTab users={list} />} />
            <Route path="regions" element={<RegionsTab />} />
            <Route path="lists" element={<ListsTab />} />
            <Route path="activity" element={<ActivityTab users={list} />} />
            <Route path="*" element={<Navigate to="requests" replace />} />
          </Routes>
        )}
      </main>
    </>
  );
}
